import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { HttpClientService } from '../services/http-client.service';

const KEY_STORAGE_KEY = 'super_admin_token';

interface SuperAdminKeyPayload {
  username?: string;
  scope?: string;
  exp?: number;
}

export interface SuperAdminSession {
  username: string;
  /** When the key stops counting: superbeheer asks to log in again after eight hours. */
  expiresAt: Date;
}

/**
 * The session of superbeheer. Its key lives under its own name in local storage, apart from a beheer or member
 * login in the same browser, and only travels with requests to /super-admin (superAdminInterceptor).
 */
@Injectable({ providedIn: 'root' })
export class SuperAdminAuthService {
  private readonly http = inject(HttpClientService);
  private readonly sessionSignal = signal<SuperAdminSession | null>(null);

  readonly session = this.sessionSignal.asReadonly();
  readonly isLoggedIn = computed(() => this.sessionSignal() !== null);

  constructor() {
    const key = readStoredKey();

    if (key !== null && !this.storeKey(key)) {
      this.logout();
    }
  }

  login(username: string, password: string): Observable<{ token: string; username: string }> {
    return this.http
      .post<{ token: string; username: string }>('/super-admin/auth/login', { username, password })
      .pipe(tap(answer => this.storeKey(answer.token)));
  }

  logout(): void {
    try {
      localStorage.removeItem(KEY_STORAGE_KEY);
    } catch {
      // Storage refused: the session below is gone all the same.
    }

    this.sessionSignal.set(null);
  }

  getKey(): string | null {
    return this.isLoggedIn() ? readStoredKey() : null;
  }

  /** Keeps a key for superbeheer; refuses one that is unreadable, expired or meant for another part. */
  private storeKey(key: string): boolean {
    const payload = decodePayload(key);

    if (!payload || payload.scope !== 'superbeheer' || !payload.exp || payload.exp * 1000 <= Date.now()) {
      return false;
    }

    try {
      localStorage.setItem(KEY_STORAGE_KEY, key);
    } catch {
      return false;
    }

    this.sessionSignal.set({ username: payload.username ?? '', expiresAt: new Date(payload.exp * 1000) });

    return true;
  }
}

function readStoredKey(): string | null {
  try {
    return localStorage.getItem(KEY_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Reads the (unverified) payload of the key; the backend checks the signature on every request. */
function decodePayload(key: string): SuperAdminKeyPayload | null {
  try {
    const segment = key.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(segment), character => character.charCodeAt(0));

    return JSON.parse(new TextDecoder().decode(bytes)) as SuperAdminKeyPayload;
  } catch {
    return null;
  }
}
